import { BedDouble, House as Home, Maximize2, Sparkles, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
export default function HomeAdditionHeroAnswer() {
  const inclusions = [{
    title: "New Bedrooms",
    description: "Custom guest rooms, children's suites, and flex rooms designed with integrated closets, natural daylighting, and code-compliant egress.",
    icon: BedDouble
  }, {
    title: "Primary Suites",
    description: "Spacious owner retreats complete with spa-inspired bathrooms, custom walk-in dressing rooms, and private outdoor access.",
    icon: Sparkles
  }, {
    title: "Square-Footage Expansion",
    description: "Seamless single- or two-story bump-outs and footprint expansions engineered to preserve structural integrity and exterior architectural rhythm.",
    icon: Maximize2
  }, {
    title: "Room-Specific Additions",
    description: "Dedicated sunrooms, light-filled home offices, enlarged family living spaces, or second-story expansions built from foundation to roofline.",
    icon: Home
  }];
  return <section id="homeadditionheroanswer" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Main Answer Column */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-secondary">
              <span className="w-6 h-px bg-primary" aria-hidden="true" />
              Brookhaven Residential Construction
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground tracking-tight leading-[1.15]">
              What does a home addition in Brookhaven involve?
            </h1>

            {/* Direct 2-3 sentence answer */}
            <div className="bg-card/70 border border-border p-6 rounded-lg shadow-xs">
              <p className="text-base sm:text-lg text-foreground/90 leading-relaxed font-default">
                A home addition in Brookhaven involves architecturally seamless expansion tailored to local zoning setbacks, tree canopy preservation, and neighborhood historic vernacular. Elevation Renovations coordinates every phase—from custom architectural engineering and DeKalb County permitting to framing, foundational integration, and luxury interior finishing. The result is valuable additional living space that looks and feels as though it was part of the original home build.
              </p>
            </div>

            {/* Inclusions Block */}
            <div className="mt-4">
              <h2 className="text-xl sm:text-2xl font-serif font-semibold text-foreground mb-4">
                What’s Included
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {inclusions.map((item, i) => {
                const Icon = item.icon;
                return <Card key={item.title} data-index={i} className="border border-border bg-card shadow-xs rounded-lg p-4 flex flex-col justify-between transition-all hover:border-primary/50">
                      <CardHeader className="p-0 pb-2 flex-row items-center gap-3">
                        <div className="p-2 rounded-md bg-accent text-accent-foreground shrink-0">
                          <Icon className="size-4 text-primary" aria-hidden="true" />
                        </div>
                        <CardTitle className="text-base font-semibold text-foreground">
                          {item.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-0">
                        <p className="text-sm text-foreground/80 leading-snug">
                          {item.description}
                        </p>
                      </CardContent>
                    </Card>;
              })}
              </div>
            </div>

            <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/ContactRequestAnEstimatePage">
                  Request an addition estimate
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/OurProjectsPage">
                  View completed projects
                </Link>
              </Button>
            </div>
          </div>

          {/* Photographic Presentation Column */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="relative rounded-xl overflow-hidden border border-border bg-muted shadow-md group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition_exterior.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition_exterior.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition_exterior.webp-wvc-sizes" alt="Two-story architectural home addition seamlessly attached to a Brookhaven residence" loading="lazy" decoding="async" className="w-full h-[380px] sm:h-[460px] object-cover object-center transition-transform duration-500 ease-out group-hover:scale-102" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <p className="text-xs uppercase tracking-wider font-semibold text-primary/90">
                  Architectural Continuity
                </p>
                <p className="text-sm font-medium drop-shadow-sm">
                  Seamless roofline transitions and matched brick masonry in Historic Brookhaven.
                </p>
              </div>
            </div>

            {/* Contextual assurance items */}
            <div className="rounded-lg border border-border bg-card p-5 shadow-xs">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                <div className="text-sm text-foreground/85 leading-relaxed">
                  <span className="font-semibold text-foreground">Permitting & Compliance:</span> Every project includes full site surveys, Brookhaven zoning board approval, and turnkey coordination with certified structural engineers.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}